import { Validator, Layout } from 'blaze-validator';
import TableManager from './TableManager';
import get from 'lodash/get';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import every from 'lodash/every';
import isPlainObject from 'lodash/isPlainObject';
import isEqual from 'lodash/isEqual';

const _ = { get, clone, cloneDeep, every, isPlainObject, isEqual };

const DEFAULT_CONTROL = 'input';

const numberTypes = ['number', 'integer', 'percent', 'currency'];

const valueFields = ['data', 'displayValue', 'originalValue', 'originalDisplayValue'];
const indexFields = [
  'calculatedIndex',
  'disabledIndex',
  'errorIndex',
  'hiddenIndex',
  'infoIndex',
  'optionsIndex',
  'validIndex',
  'warningIndex',
];

const predefinedLists = {
  months: [
    { label: 'January', value: '1' },
    { label: 'February', value: '2' },
    { label: 'March', value: '3' },
    { label: 'April', value: '4' },
    { label: 'May', value: '5' },
    { label: 'June', value: '6' },
    { label: 'July', value: '7' },
    { label: 'August', value: '8' },
    { label: 'September', value: '9' },
    { label: 'October', value: '10' },
    { label: 'November', value: '11' },
    { label: 'December', value: '12' },
  ],
};

// https://stackoverflow.com/questions/30685916/increment-a-string-with-letters
// string to number: s2n("ba") -> 26
function s2n(s) {
  let pow;
  let n = 0;
  let i = 0;
  while (i++ < s.length) {
    pow = Math.pow(26, s.length - i);
    n += (s.charCodeAt(i - 1) - 97) * pow;
  }
  return n;
}

// number to string: n2s(26) -> "ba"
function n2s(n) {
  let s = '';
  if (!n) {
    s = 'a';
  } else {
    n = parseInt(n, 10);
    while (n) {
      s = String.fromCharCode(97 + (n % 26)) + s;
      n = Math.floor(n / 26);
    }
  }
  return s;
}

class FormManager {
  /**
   * Initializes a new form manager.  An Validator instance is created and used
   * for validation.  The latest report from the validator will be stored on
   * the object under report which is initialized here.
   *
   * @param {Array} layout - The data to be validated.
   */
  constructor(layout = []) {
    this.data = {}; // Form Data
    this.predefined = {}; // Predefined Values
    this.ids = {}; // Used up field identifiers
    this.features = {};
    this.controls = {};
    this.toolbarHelpers = {};

    this.layout = new Layout();
    this.validator = new Validator();
    this.report = {};
    this.tables = {}; // Table Manager handles
    this.history = {}; // Historical snapshot to fields

    this.prefix = '';

    // new / edit / copy
    this.action = '';

    this.valueEncoder = ({ data }) => data;
    this.valueDecoder = (v) => v;

    this.opposingActions = {
      show: 'hide',
      hide: 'show',
      enable: 'disable',
      disable: 'enable',
      calculated: 'entered',
      entered: 'calculated',
    };

    this.load(layout);
  }

  /**
   * Loads page layout config and creates an object with initialized values
   * that the form can easily use.
   *
   * @param {Array} layout - Configuration definitions.
   * @return {FormManager}
   */
  load(layout = []) {
    const form = {};
    this.layout.load(layout);
    this.data = form;

    // Initialize input fields
    this.layout.getAllInputFields().forEach((fieldName) => {
      const field = this.layout.lookup[fieldName];

      form[fieldName] = this.initFieldDefaults(field);

      // Set field to readonly if it has initial data
      if (field.readOnlyOnInit) {
        if (field.data) {
          form[fieldName].readOnly = true;
        }
      }

      // Set field to disabled if click to edit and has initial data
      if (field.clickToEdit && field.data) {
        form[field.name].disabled = true;
      }

      // TODO unclear collection double meaning this should be removed
      if (form[fieldName].control.startsWith('tags')) {
        form[fieldName].collection = true;
      }

      // Init Textareas (not inside Table) that are Arrays to use newlines instead
      // TODO better if this came up to the frontend normally in the first place
      if (form[fieldName].control === 'textarea' && !form[fieldName].insideTable) {
        valueFields.forEach((key) => {
          if (Array.isArray(form[fieldName][key])) {
            form[fieldName][key] = form[fieldName][key].join('\n');
          }
        });
      }

      if ((form[fieldName].type === 'file' || form[fieldName].control === 'upload') && !form[fieldName].insideTable) {
        valueFields.forEach((key) => {
          if (typeof form[fieldName][key] === 'string' && form[fieldName][key]) {
            form[fieldName][key] = { filename: form[fieldName][key] };
          }
        });
      }

      // Handle fields that want to reference predefined values
      if (field.predefined) {
        const predefinedData = _.get(this.predefined, form[fieldName].data);
        valueFields.forEach((key) => {
          form[fieldName][key] = predefinedData;
        });
      }

      // Handle dataAlts
      if (field.dataAlt) {
        form[fieldName].displayValue = this.encodeFieldValue(field);
        form[fieldName].originalDisplayValue = this.encodeFieldValue(field);
      }
    });

    this.initAllTables();

    // Handle tables
    this.layout.forEachTable((tableName) => {
      this.layout.getAllFieldsUnderGroup(tableName).forEach((fieldName) => {
        if (form[fieldName].aggregated) {
          return;
        }

        form[fieldName].collection = true;

        // Init Textareas (inside Table) that are Arrays to use newlines instead
        // TODO better if this came up to the frontend normally in the first place
        if (form[fieldName].control === 'textarea') {
          valueFields.forEach((key) => {
            form[fieldName][key].forEach((data, dataIndex) => {
              if (Array.isArray(data)) {
                form[fieldName][key][dataIndex] = data.join('\n');
              }
            });
          });
        }

        // Upload/file field inside collection.  I don't think this is actually used anywhere
        if (form[fieldName].type === 'file' || form[fieldName].control === 'upload') {
          valueFields.forEach((key) => {
            form[fieldName][key].forEach((data, index) => {
              if (typeof data === 'string') {
                form[fieldName][key][index] = { filename: data };
              }
            });
          });
        }

        // Experimental
        // CREATE NEW FIELDS
        // this.createTableField(fieldName);

        // Fill in defaults for each 'row'
        const { data } = form[fieldName];

        form[fieldName].disabledIndex = data.map(() => !!form[fieldName].disabled);
        form[fieldName].hiddenIndex = data.map(() => !!form[fieldName].hidden);
        form[fieldName].calculatedIndex = data.map(() => !!form[fieldName].calculate);
        form[fieldName].infoIndex = data.map(() => '');
        form[fieldName].touchedIndex = data.map(() => false);
        form[fieldName].warningIndex = data.map(() => '');

        if (form[fieldName].options) {
          // Check if options is an array of arrays and use that as the optionsIndex
          if (Array.isArray(form[fieldName].options[0])) {
            form[fieldName].optionsIndex = data.map((d, idx) =>
              Array.isArray(form[fieldName].options[idx]) ? form[fieldName].options[idx] : []
            );
          } else {
            form[fieldName].optionsIndex = data.map(() => form[fieldName].options);
          }
        }
      });
    });

    this.initAllOptions().initAllOptionValues();

    // Check for weirdness, these things shouldn't happen often
    this.eachField((fieldName) => {
      if (!form[fieldName].control) {
        console.warn(`Field: ${fieldName} has an invalid control`, form[fieldName]);
      }

      if (form[fieldName].data === undefined) {
        console.warn(`Field: ${fieldName} has undefined data`);
        this.resetFieldValue(fieldName);
      }
    });

    this.initKeyValueItems();

    return this;
  }

  createTableField(fieldName) {
    const form = this.data;
    const field = form[fieldName];

    field.data.forEach((data, index) => {
      const newFieldName = `${fieldName}-${index + 1}`;
      form[newFieldName] = this.initFieldDefaults({ ...field, data, name: newFieldName });
    });

    return field;
  }

  /**
   * Init defaults for each key in a single field
   *
   * @param {Object} field - Field properties.
   * @return {Object}
   */
  initFieldDefaults(field) {
    const identifier = field.identifier ? `${this.prefix}${field.identifier}` : this.generateId(field.label);

    const initializedField = {
      ...field,
      identifier,

      // Field Status Markers
      dirty: false,
      info: field.info || '',
      invalidValue: null,
      needsValidation: false,
      touched: false,
      valid: true,
      validError: '',
      warning: field.warning || '',

      // Default Strings
      label: field.label || '',

      // Field Descriptions
      aggregated: !!field.aggregated,
      calculated: !!field.calculate,
      dependencies: field.dependencies || null,
      selfValidation: field.validation !== undefined,

      // Objects
      validation: _.cloneDeep(field.validation),
    };

    const defaultValue = field.collection ? [] : '';
    const initializedValue =
      field.data === undefined || field.data === null || (this.action === 'copy' && field.noCopy === true)
        ? defaultValue
        : field.data;

    valueFields.forEach((valueField) => {
      initializedField[valueField] = _.cloneDeep(initializedValue);
    });

    indexFields.forEach((indexField) => {
      if (indexField !== 'optionsIndex') {
        initializedField[indexField] = [];
      }
    });

    initializedField.control = this.determineControl(initializedField).toLowerCase();

    return initializedField;
  }

  /**
   * Initialization tasks that need to be performed after the form object is loaded.
   * Updates references to fields with their values.
   *
   * @return {FormManager}
   */
  initKeyValueItems() {
    const form = this.getData();

    this.eachField((fieldName) => {
      const field = form[fieldName];

      if (field && field.validation) {
        const { key, filter, list } = field.validation;

        if (key && form[key]) {
          field.validation.keyValue = _.clone(form[key].data);
        }

        const listMethodKey = _.get(list, 'method.key');
        if (listMethodKey && form[listMethodKey]) {
          field.validation.list.method.keyData = _.clone(form[listMethodKey].data);
        }

        if (filter && form[filter]) {
          field.validation.filter = form[filter].data;
        }
      }

      ['click', 'blur', 'check', 'uncheck'].forEach((eventName) => {
        const eventsUpdate = _.get(field, `events.${eventName}.update`);
        if (eventsUpdate) {
          eventsUpdate.forEach((rule) => {
            const methodKeys = _.get(rule, 'method.keys');
            if (methodKeys) {
              rule.method.keyData = [];
              methodKeys.forEach((key, keyIndex) => {
                if (form[key]) {
                  rule.method.keyData[keyIndex] = _.clone(form[key].data);
                } else {
                  rule.method.keyData[keyIndex] = key;
                }
              });
            }
          });
        }
      });
    });

    return this;
  }

  initBlurOptions() {
    const form = this.getData();

    this.eachField((fieldName) => {
      if (_.get(form[fieldName], 'events.blur.update')) {
        form[fieldName].events.blur.update.forEach((rule) => {
          if (rule.options) {
            rule.field.forEach((name) => {
              if (form[rule.options].insideTable || form[rule.options].collection) {
                const options = [];
                form[rule.options].data.forEach((v, idx) => {
                  options.push({
                    label: form[rule.options].displayValue[idx],
                    value: form[rule.options].data[idx],
                  });
                });

                form[name].options = options;
              } else {
                form[name].options = [
                  {
                    label: form[rule.options].displayValue,
                    value: form[rule.options].data,
                  },
                ];
              }
            });
          }
        });
      }
    });
  }

  /**
   * Sets a given field by name as invalid.  Used for validation issues outside
   * of native validate()
   *
   * @param {String} fieldName - Name of the field.
   * @param {String} reason - Reason for the field being invalid.
   * @param {Number} index - Index in the event the data is an array.
   * @return {Boolean} - true when the field exists, false when it does not
   */
  setInvalid(fieldName, reason = '', index = 0) {
    const form = this.getData();

    if (!form[fieldName]) {
      FormManager.warnFieldName(fieldName);
      return false;
    }

    if (Array.isArray(form[fieldName].displayValue)) {
      const badValue = !FormManager.isEmpty(form[fieldName].displayValue[index])
        ? form[fieldName].displayValue[index]
        : '<Empty>';

      reason = `${reason} [${badValue}]`;
    }

    form[fieldName].valid = false;
    form[fieldName].validError = `${form[fieldName].validError} ${reason}`;
    form[fieldName].validIndex[index] = false;
    form[fieldName].errorIndex[index] = reason;

    this.data = form;

    return true;
  }

  /**
   * Register toolbar helper by passing in a name and component
   * name of 'default' will be used if helper isn't found
   * @param {String} name
   * @param {Object} component
   */
  registerToolbarHelper({ name, component }) {
    this.toolbarHelpers[name.toLowerCase()] = component;
  }

  /**
   * Register control by passing in a name and component
   * @param {String} name
   * @param {Object} component
   */
  registerControl({ name, component }) {
    this.controls[name.toLowerCase()] = component;
  }

  /**
   * Get a list of registered control names
   */
  getControlNames() {
    return Object.keys(this.controls);
  }

  /**
   * Sets the entire form as dirty.  This can be data that was pre-populated but
   * not yet persisted anywhere.
   *
   * @return {FormManager}
   */
  dirtyAll() {
    const form = this.getData();

    this.eachField((fieldName) => {
      const field = form[fieldName];

      if (Array.isArray(field.data) || field.collection) {
        field.originalDisplayValue = [];
        field.originalValue = [];
      } else {
        field.originalDisplayValue = '';
        field.originalValue = '';
      }

      field.dirty = field.data !== null;
      field.needsValidation = field.data !== null;
    });

    this.data = form;
    return this;
  }

  /**
   * Sets the entire form as pristine.  Typically done after the form was
   * successfully persisted somewhere.
   *
   * @return {FormManager}
   */
  pristineAll() {
    const form = this.getData();

    this.eachField((fieldName) => {
      form[fieldName].dirty = false;
      form[fieldName].originalDisplayValue = _.cloneDeep(form[fieldName].displayValue);
      form[fieldName].originalValue = _.cloneDeep(form[fieldName].data);
    });

    this.data = form;
    return this;
  }

  /**
   * Update a value for a given field.  By default this will set the data to be
   * dirty.  keepPristine allows for an override.
   *
   * @param {String} fieldName - Name of the field.
   * @param {Array|String} value - Value to assign.
   * @param {Boolean} keepPristine - Assigns value without dirtying record.
   * @return {FormManager}
   */
  setValue(fieldName, value, keepPristine = false) {
    const form = this.getData();
    const field = form[fieldName];
    const newValue = this.decodeFieldValue(value);
    let dirty = true;

    if (!field) {
      FormManager.warnFieldName(fieldName);
      return this;
    }

    // See if value is actually changing (non-collections only)
    if (!field.collection) {
      if (field.originalValue === newValue) {
        dirty = false;
      }
    }

    field.displayValue = _.clone(value);
    field.data = _.clone(newValue);

    if (!keepPristine) {
      field.dirty = dirty;
      field.needsValidation = true;
    } else {
      field.originalValue = newValue;
      field.originalDisplayValue = _.clone(value);
    }

    this.data = form;
    return this;
  }

  /**
   * Get data for given field name.
   *
   * @param {String} fieldName - Name of the field.
   * @return {String|Array} - Field data.
   */
  getValue(fieldName) {
    return this.getData()[fieldName].data;
  }

  /**
   * Get an attribute from a given field.
   *
   * @param {String} fieldName - Name of the field.
   * @param {String} attribute - Name of the attribute.
   * @param {Number} dataIndex - Optional data index.
   * @return {Boolean} - Attribute data.
   */
  getAttribute(fieldName, attribute, dataIndex) {
    const form = this.getData();

    if (!form[fieldName]) {
      FormManager.warnFieldName(fieldName);
      return;
    }

    if (dataIndex !== undefined) {
      if (form[fieldName][`${attribute}Index`] === undefined) {
        console.warn(`Bad Attribute ${attribute} on ${fieldName}`);
        return;
      }
      return form[fieldName][`${attribute}Index`][dataIndex];
    }

    if (form[fieldName][attribute] === undefined) {
      console.warn(`Bad Attribute ${attribute} on ${fieldName}`);
      return;
    }

    return form[fieldName][attribute];
  }

  /**
   * Locally sets an arbitrary object of data so that it can be used as a
   * reference to field data if needed.
   *
   * @param {Object} data - Form object literal.
   * @return {FormManager}
   */
  setPredefined(data = {}) {
    this.predefined = _.cloneDeep(data);
    return this;
  }

  /**
   * Loads form data into the FormManager.  Usually taken from React state.
   *
   * @param {Object} data - Form object literal.
   * @return {FormManager}
   */
  setData(data = {}) {
    this.data = _.cloneDeep(data);
    return this;
  }

  /**
   * Initializes display values based on validator results and dataAlt settings.
   *
   * @return {FormManager}
   */
  init(step = null) {
    this.initAllTables().initAllOptions().initAllOptionValues().initBlurOptions();

    const layout = this.layout.getLayout();
    const report = this.validator.load(layout, this.gatherFormData()).validate();

    const form = this.getData();
    const fieldsChecked = Object.keys(report.results);

    // When we are validating steps, we'll only go through the ones for that step
    let stepKey;
    if (step !== null) {
      stepKey = this.layout.steps[step].key;
    }

    fieldsChecked.forEach((fieldName) => {
      const field = form[fieldName];
      const fieldReport = report.results[fieldName];
      // Handle dependencyActions (show/hide/enable/disable)
      if (fieldReport && fieldReport.dependencyActions) {
        fieldReport.dependencyActions.forEach((actions, index) => {
          actions.forEach((action) => {
            if (field && field.collection) {
              return this[action](fieldName, index);
            }

            return this[action](fieldName);
          });
        });
      }

      // Currently groups aren't in the form object
      if (!field) {
        return;
      }

      // Check for steps
      if (step !== null && field.insideStep !== stepKey) {
        return;
      }

      // Handle dataAlts
      if (field.dataAlt) {
        field.displayValue = this.encodeFieldValue(field);
        field.originalDisplayValue = this.encodeFieldValue(field);
        return;
      }

      // Handle validated/sanitized data
      if (report.results[fieldName].valid === true) {
        field.displayValue = _.clone(report.results[fieldName].displayValue);
        field.originalDisplayValue = _.clone(report.results[fieldName].displayValue);

        // TODO Probably can do this for all fields not just calculated
        if (field.calculated) {
          if (!field.noSubmit && !_.isEqual(report.results[fieldName].value, field.data)) {
            field.dirty = true;
            console.log(
              `Possible new calculated field added to layout: ${field.name} (${field.data} -> ${report.results[fieldName].value})`
            );
          }

          field.data = _.clone(report.results[fieldName].value);
          field.originalValue = _.clone(report.results[fieldName].value);
        }
      } else {
        field.needsValidation = true;
      }
    });

    return this;
  }

  initAllOptions() {
    this.eachField((fieldName) => {
      this.initOptions(fieldName);
    });

    return this;
  }

  initOptions(fieldName) {
    const form = this.getData();
    const field = form[fieldName];

    if (!field) {
      FormManager.warnFieldName(fieldName);
      return this;
    }

    if (field.control === 'checkbox' && !field.options) {
      field.options = ['Y', 'N'];
    }

    if (!field.options || !field.options.length) {
      return this;
    }

    const sanitizeOptions = (options) => {
      // Handle references to predefined lists
      if (typeof options === 'string') {
        if (predefinedLists[options]) {
          options = predefinedLists[options];
        } else {
          console.warn(`Fieldname ${fieldName} with options value ${options} is not a predefined list`);
          options = [options];
        }
      }

      // Ensure it's an array
      if (!Array.isArray(options)) {
        options = [options];
      }

      // Ensure it's in label/value object format (when it's not a checkbox)
      if (field.control !== 'checkbox') {
        options.forEach((option, optionIndex) => {
          if (!_.isPlainObject(option)) {
            options[optionIndex] = { label: option, value: option };
          }
        });
      }

      return options;
    };

    if (field.options) {
      if (Array.isArray(field.options[0])) {
        field.options.forEach((options, index) => {
          field.options[index] = sanitizeOptions(options);
        });
      } else {
        field.options = sanitizeOptions(field.options);
      }
    }

    if (field.insideTable) {
      if (!field.optionsIndex) {
        field.optionsIndex = [];
      }

      if (Array.isArray(field.options[0])) {
        field.data.forEach((data, index) => {
          field.optionsIndex[index] = Array.isArray(field.options[index]) ? field.options[index] : [];
        });
      } else {
        field.data.forEach((data, index) => {
          field.optionsIndex[index] = field.options;
        });
      }
    }

    return this;
  }

  initAllOptionValues() {
    this.eachField((fieldName) => {
      this.initOptionValue(fieldName);
    });

    return this;
  }

  initOptionValue(fieldName) {
    const form = this.getData();
    const field = form[fieldName];

    if (!field) {
      FormManager.warnFieldName(fieldName);
      return this;
    }

    if (!field.options || !field.options.length) {
      return this;
    }

    const hasOption = (value, options) => {
      for (let i = 0; i < options.length; i++) {
        // Object defined options
        if (options[i].value !== undefined && options[i].value === value) {
          return true;
        }

        // Checkbox/Shorthand
        if (options[i] === value) {
          return true;
        }
      }

      return false;
    };

    const defaultValue = this.getDefaultValue(fieldName);
    if (!field.collection) {
      // TODO Super hacky ;(
      if (field.options.length && field.control !== 'reactselect') {
        if (!hasOption(field.data, field.options)) {
          valueFields.forEach((key) => {
            field[key] = _.clone(defaultValue);
          });
        }
      }
    } else {
      if (!field.data || !Array.isArray(field.data)) {
        return this;
      }

      // TODO consider caching for hasValue
      field.data.forEach((data, dataIndex) => {
        // TODO Super hacky ;(
        if (field.options.length && field.control !== 'reactselect') {
          if (!hasOption(data, field.optionsIndex[dataIndex])) {
            valueFields.forEach((key) => {
              field[key][dataIndex] = _.clone(defaultValue);
            });
          }
        }
      });
    }

    return this;
  }

  initAllTables() {
    this.layout.forEachTable((tableName) => {
      this.initTable(tableName);
    });

    return this;
  }

  /**
   * Sets the following three variables
   * minListSize - minimum required rows for submission
   * defaultSize - starting number of rows for a table
   * initialSize - starting row count based on initial data, minListSize, and defaultSize
   *
   * Also sets defaultValues in the event the table grows and no data is set.
   *
   * @return {FormManager}
   */
  initTable(tableName) {
    const form = this.getData();
    const tableConfig = this.layout.lookup[tableName];

    // Find implicit defaultSize based on minListSize
    if (tableConfig.minListSize !== undefined || tableConfig.defaultSize !== undefined) {
      if (tableConfig.defaultSize < tableConfig.minListSize) {
        tableConfig.defaultSize = tableConfig.minListSize;
      } else {
        tableConfig.defaultSize = tableConfig.minListSize || tableConfig.defaultSize;
      }
    }

    const fieldNameList = this.layout
      .getAllFieldsUnderGroup(tableName)
      .filter((fieldName) => !form[fieldName].aggregated);

    // Find implicit initialSize based on largest data array
    let max = 0;
    fieldNameList.forEach((fieldName) => {
      const field = form[fieldName];

      valueFields.forEach((key) => {
        if (!field[key]) {
          field[key] = [];
          return;
        }

        if (!Array.isArray(field[key])) {
          console.warn(`Initializing ${fieldName} ${key} as an Array`);
          field[key] = [field[key]];
        }
      });

      if (max < field.data.length) {
        max = field.data.length;
      }
    });

    tableConfig.initialSize = tableConfig.defaultSize > max ? tableConfig.defaultSize : max;

    fieldNameList.forEach((fieldName) => {
      const { initialSize } = tableConfig;
      if (form[fieldName].data.length < initialSize) {
        for (let dataIndex = form[fieldName].data.length; dataIndex < initialSize; dataIndex++) {
          valueFields.forEach((key) => {
            form[fieldName][key][dataIndex] = this.getDefaultValue(fieldName);
          });
        }
      }
    });

    // Create Table Manager
    this.tables[tableName] = new TableManager(tableConfig.initialSize, tableName);
    this.tables[tableName].fieldNameList = [...fieldNameList];
    this.tables[tableName].threshold = tableConfig.threshold;
    this.tables[tableName].limit = tableConfig.limit;

    return this;
  }

  /**
   * Validates all data in the form object in which needsValidation is set to true
   *
   * @return {FormManager}
   */
  validate(step = null) {
    if (window.VALIDATOR_DEBUG) {
      console.warn('<Validator> PageLayout used', this.layout.getLayout());
      console.warn('<Validator> Form state before sanitation', _.cloneDeep(this.getData()));
    }

    const layout = this.layout.getLayout();
    const report = this.validator.load(layout, this.gatherFormData()).validate();

    const form = this.getData();
    const fieldsChecked = Object.keys(report.results);

    // When we are validating steps, we'll only go through the ones for that step
    let stepKey;
    if (step !== null) {
      stepKey = this.layout.steps[step].key;
    }

    fieldsChecked.forEach((fieldName) => {
      const field = form[fieldName];
      const fieldReport = report.results[fieldName];

      // Handle dependencyActions (show/hide/enable/disable)
      if (fieldReport && fieldReport.dependencyActions) {
        report.results[fieldName].dependencyActions.forEach((actions, index) => {
          actions.forEach((action) => {
            if (field && field.collection) {
              return this[action](fieldName, index);
            }

            return this[action](fieldName);
          });
        });
      }

      // Currently groups aren't in the form object
      if (!field) {
        return;
      }

      // Check for steps
      if (step !== null && field.insideStep !== stepKey) {
        return;
      }

      // Skip fields that don't validate
      // TODO fix this shit (json and file)
      if ((field.type === 'file' || field.control === 'button' || field.control === 'paragraph') && !field.calculated) {
        return;
      }

      // TODO Turning this off for now.  May not need at the moment
      // if (!form[name].needsValidation && !form[name].calculated) {
      //   // console.warn(`Skipping ${name}`);
      //   continue;
      // }

      field.needsValidation = false;

      if (!fieldReport) {
        return;
      }

      // Don't clear "backend" validation problems unless the value changed
      if (fieldReport.valid && field.invalidValue !== null && field.invalidValue === fieldReport.value) {
        return;
      }

      field.valid = fieldReport.valid;

      // Track invalid value
      if (!fieldReport.valid) {
        field.invalidValue = field.data;
      } else {
        field.invalidValue = null;
      }

      // Set display value from validator if an alternate isn't already provided
      // or if it provided its own validation (usually from search lists)
      if (fieldReport.valid && !field.dataAlt && !field.selfValidation) {
        field.displayValue = fieldReport.displayValue;
      }

      // TODO validator should have a method to build a string
      field.validError = fieldReport.valid ? '' : fieldReport.errors[0].reason;
      field.data = fieldReport.value;
      field.validIndex = _.clone(fieldReport.validIndex) || [];
      field.errorIndex = _.clone(fieldReport.errorIndex) || [];

      // Checks for a collection of values and updates the displayValues for ones that passed
      if (!fieldReport.valid && field.validIndex.length > 1) {
        field.validIndex.forEach((validResult, index) => {
          if (validResult && !field.dataAlt) {
            field.displayValue[index] = fieldReport.displayValue[index];
          }
        });
      }
    });

    // Handle dependencies for Groups (just tables for now)
    this.layout.forEachGroup((groupName, config) => {
      const groupReport = report.results[groupName];

      // Handle dependencyActions (show/hide/enable/disable)
      if (groupReport && groupReport.dependencyActions) {
        report.results[groupName].dependencyActions.forEach((actions) => {
          actions.forEach((action) => {
            return this[action](groupName);
          });
        });
      }
    });

    this.report = report;

    if (window.VALIDATOR_DEBUG) {
      console.warn('<Validator> Report', report);
      console.warn('<Validator> Form state after sanitation', _.cloneDeep(this.getData()));
      console.warn('<Validator> Set VALIDATOR_DEBUG to false to turn off these warnings');
    }

    return this;
  }

  /**
   * Returns the form data as an object literal in the same format in which it was
   * loaded with setData.
   *
   * @return {FormManager}
   */
  getData() {
    return this.data;
  }

  /**
   * Exports form data in a format that used for persisting data in D3 or
   * processed by the Validator.
   *
   * @return {Array} - List of object with specific keys (key order also matters)
   */
  exportData(onlyChanges = false) {
    const result = [];

    this.eachField((fieldName, field) => {
      let dirty = field.dirty;

      // Check for fields whose value changed but didn't get marked as dirty
      // These are usually newly added calculation fields
      if (!dirty && !_.isEqual(field.originalValue, field.data)) {
        console.log(`Possible new field: ${fieldName}`);
        dirty = true;
      }

      if (onlyChanges && !dirty) {
        return;
      }

      if (field.noSubmit) {
        return;
      }

      result.push({
        // collection: _.get(form[name], 'collection') ? 'yes' : 'no',
        collection: Array.isArray(field.data) ? 'yes' : 'no',
        name: fieldName,
        data: field.data,
      });
    });

    return result;
  }

  /**
   * Basically exportData without noSubmit.  Used by validator and may change in future
   *
   * @return {Array} - List of object with specific keys (key order also matters)
   */
  gatherFormData() {
    const result = [];

    this.eachField((fieldName, field) => {
      result.push({
        collection: Array.isArray(field.data) ? 'yes' : 'no',
        name: fieldName,
        data: field.data,
      });
    });

    return result;
  }

  /**
   * Exports data in a key value pair object.
   *
   * @return {Object} - Form data only.
   */
  exportDataObject(onlyChanges = false, useTypedData = false) {
    const result = {};

    this.eachField((fieldName, field) => {
      let dirty = field.dirty;

      // Check for fields whose value changed but didn't get marked as dirty
      // These are usually newly added calculation fields
      if (!dirty && !_.isEqual(field.originalValue, field.data)) {
        console.log(`Possible new field: ${fieldName}`);
        dirty = true;
      }

      if (onlyChanges && !dirty) {
        return;
      }

      if (field.noSubmit) {
        return;
      }

      let formattedData = field.data;

      if (useTypedData) {
        if (numberTypes.some((t) => t === field.type)) {
          formattedData = formattedData / 1;
        }
      }

      result[fieldName] = formattedData;
    });

    return result;
  }

  /**
   * Reset fields that have the resetOnSubmit flag
   *
   * @return {FormManager}
   */
  clearFieldsThatNeedIt() {
    this.eachField((fieldName, field) => {
      if (field.resetOnSubmit) {
        this.resetFieldValue(fieldName);
      }
    });

    return this;
  }

  /**
   * Encodes a field value that has dataAlt defined.
   *
   * @param {Object} field - Field object (from layout)
   * @return {String} - New field value.
   */
  encodeFieldValue(field) {
    if (!field.dataAlt) {
      return _.cloneDeep(field.data);
    }

    if (Array.isArray(field.dataAlt)) {
      return field.data.map((data, index) =>
        this.valueEncoder({
          data: field.data[index],
          dataAlt: field.dataAlt[index],
        })
      );
    }

    return this.valueEncoder({ data: field.data, dataAlt: field.dataAlt });
  }

  /**
   * Decodes a field value that has a special item delimiter defined.
   *
   * @param {String} value Field value.
   * @return {String} value New field value.
   */
  decodeFieldValue(value) {
    if (Array.isArray(value)) {
      return value.map((val) => this.decodeFieldValue(val));
    }

    // Decode will only happen if value is a String
    if (!value || !value.match) {
      return value;
    }

    return this.valueDecoder(value);
  }

  /**
   * Essentially clears a field's value or sets the default option
   *
   * @param {String} fieldName Field name.
   * @param {Number} dataIndex
   * @return {FormManager}
   */
  resetFieldValue(fieldName, dataIndex) {
    const field = this.getData()[fieldName];

    if (!field) {
      FormManager.warnFieldName(fieldName);
      return this;
    }

    const defaultValue = this.getDefaultValue(fieldName);
    if (dataIndex !== undefined) {
      field.data[dataIndex] = _.clone(defaultValue);
      field.displayValue[dataIndex] = _.clone(defaultValue);
    } else {
      if (field.insideTable) {
        field.data.forEach((data, index) => {
          field.data[index] = _.clone(defaultValue);
          field.displayValue[index] = _.clone(defaultValue);
        });
      } else {
        field.data = _.clone(defaultValue);
        field.displayValue = _.clone(defaultValue);
      }
    }

    return this;
  }

  getDefaultValue(fieldName) {
    const field = this.getData()[fieldName];

    if (!field) {
      FormManager.warnFieldName(fieldName);
      return null;
    }

    const { control, options, noEmptyOption } = field;

    if (control && control.startsWith('tags')) {
      return [];
    }

    if (control === 'checkbox') {
      // TODO hack to fix call before options init
      if (!options) {
        return 'N';
      }

      return options[1]; // Unchecked option (['Y', 'N'])
    }

    if (options && options.length && noEmptyOption) {
      return options[0].value; // { label: 'Red', value: 'red' }
    }

    return '';
  }

  /**
   * Adds a new row in a collection
   *
   * TODO: In the future it'd be nice to give a collection name and not an arbitrary
   * field list.
   * @param {Array} fieldList - Field name.
   * @return {FormManager}
   */
  addRow(tableName, newIndex) {
    const tableManager = this.tables[tableName];
    const form = this.getData();
    const fieldsThatAutoed = []; // TODO: May not be required anymore -- phase out

    tableManager.fieldNameList.forEach((fieldName) => {
      // Set values for new rows to empty string but it can change based on
      // auto populate rules
      let value = this.getDefaultValue(fieldName);
      let displayValue = this.getDefaultValue(fieldName);

      // Fields can have multiple auto populate rules, they are executed in the order
      // they are defined, the last applicable one will end up being applied.
      let populateLogic = form[fieldName].autoPopulate;
      if (!Array.isArray(populateLogic)) {
        populateLogic = populateLogic === undefined ? [] : [populateLogic];
      }

      const lastIndex = form[fieldName].data.length - 1;

      // Check for any auto-populate logic
      populateLogic.forEach((logicKey) => {
        if (!logicKey) {
          return;
        }

        // Using the last value only if it's the second row at least and there's data in the previous row
        if (logicKey === 'last' && form[fieldName].data.length > 0) {
          const lastDisplayValue = form[fieldName].displayValue[lastIndex];
          const lastRawValue = form[fieldName].data[lastIndex];

          // Check to see if a value exists and use it if it does
          if (lastDisplayValue || lastRawValue) {
            displayValue = lastDisplayValue;
            value = lastRawValue;
            fieldsThatAutoed.push(fieldName);
          }

          return;
        }

        if (logicKey === 'increment') {
          // Assuming 1?
          if (form[fieldName].data.length === 0) {
            const newValue = '1';
            displayValue = newValue;
            value = newValue;
            fieldsThatAutoed.push(fieldName);
            return;
          }

          const lastRawValue = form[fieldName].data[lastIndex];

          // Numeric
          if (/^[1-9]+\d*$/.test(lastRawValue)) {
            const newValue = parseFloat(lastRawValue) + 1;
            displayValue = newValue;
            value = newValue;
            fieldsThatAutoed.push(fieldName);
            return;
          }

          // Alpha
          if (/^[A-Za-z]+$/.test(lastRawValue)) {
            const newValue = n2s(parseFloat(s2n(lastRawValue.toLowerCase())) + 1);

            if (/^[A-Z]+$/.test(lastRawValue)) {
              displayValue = newValue.toUpperCase();
              value = newValue.toUpperCase();
            } else {
              displayValue = newValue;
              value = newValue;
            }
            fieldsThatAutoed.push(fieldName);
            return;
          }

          // AlphaNumeric & leading zero strings
          if (/^([0A-Za-z\s#]+)(\d+)+$/.test(lastRawValue)) {
            const matches = lastRawValue.match(/^([0A-Za-z\s#]+)(\d+)+$/);
            const prefix = matches[1];
            const num = matches[2];
            const newValue = `${prefix}${parseFloat(num) + 1}`;
            displayValue = newValue;
            value = newValue;
          }
        }

        // Check to see if defined logic matches a field name and then use that data
        if (logicKey && form[logicKey] && form[logicKey].data) {
          displayValue = form[logicKey].displayValue;
          value = form[logicKey].data;
          fieldsThatAutoed.push(fieldName);
        }
      });

      // Set hidden based on dependency being defined TODO: Actually run dependency!
      let hidden = !!form[fieldName].hidden;

      if (form[fieldName].dependencies && form[fieldName].hiddenIndex[0] !== undefined) {
        hidden = form[fieldName].hiddenIndex[0];
      }

      newIndex = newIndex !== undefined ? newIndex : lastIndex;

      form[fieldName].data.splice(newIndex, 0, value);
      form[fieldName].displayValue.splice(newIndex, 0, displayValue);
      form[fieldName].validIndex.splice(newIndex, 0, true);
      form[fieldName].errorIndex.splice(newIndex, 0, '');
      form[fieldName].hiddenIndex.splice(newIndex, 0, hidden);
      form[fieldName].warningIndex.splice(newIndex, 0, '');
      form[fieldName].calculatedIndex.splice(newIndex, 0, !!form[fieldName].calculate); // TODO Dep?

      if (form[fieldName].optionsIndex) {
        form[fieldName].optionsIndex.splice(newIndex, 0, form[fieldName].options);
      }
    });

    this.data = form;

    return this;
  }

  /**
   * Removes an existing row in a collection
   *
   * TODO: In the future it'd be nice to give a collection name and not an arbitrary
   * field list.
   * @param {Array} fieldList - Field name.
   * @param {Number} dataIndex
   * @return {FormManager}
   */
  removeRow(fieldList = [], dataIndex) {
    if (!Array.isArray(fieldList)) {
      fieldList = [fieldList];
    }

    const form = this.getData();

    fieldList.forEach((fieldName) => {
      [...valueFields, ...indexFields].forEach((key) => {
        if (form[fieldName][key]) {
          form[fieldName][key].splice(dataIndex, 1);
        }
      });

      form[fieldName].dirty = true;
    });

    this.data = form;
    return this;
  }

  /**
   * Sets the touch flag to true for the given field.
   *
   * @param {String} fieldName - Field name.
   * @param {Number} dataIndex - DataIndex if inside collection.
   * @return {FormManager}
   */
  touch(fieldName, dataIndex) {
    const form = this.getData();

    if (dataIndex !== undefined) {
      form[fieldName].touchedIndex[dataIndex] = true;
    } else {
      form[fieldName].touched = true;
    }

    this.data = form;
    return this;
  }

  /**
   * Sets the touch flag for all fields.
   *
   * @param {Number} step - Step if applicable.
   * @return {FormManager}
   */
  touchAll(step) {
    const touch = (field) => {
      if (!field) {
        return;
      }

      field.touched = true;
      if (field.collection && Array.isArray(field.data)) {
        field.touchedIndex = field.data.map(() => true);
      }
    };

    // Handle Step
    if (this.layout.hasSteps && this.layout.steps[step]) {
      const form = this.getData();
      const { key } = this.layout.steps[step];
      this.layout.getAllFieldsUnderGroup(key).forEach((fieldName) => {
        touch(form[fieldName]);
      });
      return this;
    }

    this.eachField((fieldName, field) => {
      touch(field);
    });

    return this;
  }

  clearTable(tableName) {
    const form = this.getData();

    this.layout.getAllFieldsUnderGroup(tableName).forEach((fieldName) => {
      if (form[fieldName].aggregated) {
        return;
      }
      valueFields.forEach((valueField) => {
        form[fieldName][valueField] = [];
      });

      // TODO Clear History
    });

    this.data = form;
    return this;
  }

  clearTables() {
    this.layout.forEachTable((tableName) => {
      this.clearTable(tableName);
    });
  }

  /**
   * Sets meta data to show a field.
   *
   * @param {String} fieldName Field name.
   * @param {String} [dataIndex=undefined] Index for a collection.
   * @return {FormManager}
   */
  show(fieldName, dataIndex) {
    // console.log(`Showing ${fieldName} ${dataIndex}`);
    if (Layout.isGroup(this.layout.lookup[fieldName].type)) {
      this.layout.lookup[fieldName].hidden = false;
      return this;
    }

    const form = this.getData();

    if (dataIndex !== undefined && form[fieldName].hiddenIndex[dataIndex] !== undefined) {
      form[fieldName].hiddenIndex[dataIndex] = false;
    }

    // Update hidden to reflect state of hiddenIndex
    if (form[fieldName].collection && form[fieldName].hiddenIndex.length) {
      if (_.every(form[fieldName].hiddenIndex, (v) => v === true)) {
        form[fieldName].hidden = true;
        form[fieldName].allHidden = true;
      } else {
        form[fieldName].hidden = false;
        form[fieldName].allHidden = false;
      }
    }

    form[fieldName].hidden = false;

    return this;
  }

  /**
   * Sets meta data to hide a field.
   *
   * @param {String} fieldName Field name.
   * @param {String} [dataIndex=undefined] Index for a collection.
   * @return {FormManager}
   */
  hide(fieldName, dataIndex) {
    // console.log(`Hiding ${fieldName} ${dataIndex}`);
    if (Layout.isGroup(this.layout.lookup[fieldName].type)) {
      this.layout.lookup[fieldName].hidden = true;
      return this;
    }

    const form = this.getData();

    if (dataIndex !== undefined) {
      if (form[fieldName].hiddenIndex[dataIndex] !== undefined) {
        form[fieldName].hiddenIndex[dataIndex] = true;
      } else {
        form[fieldName].hidden = true;
      }
    } else {
      form[fieldName].hidden = true;
      // Problematic, don't know what this line was originally meant to solve (probably hidden stuff shouldn't be saved or something weird)
      // form[fieldName].dirty = false;
    }

    // Update hidden to reflect state of hiddenIndex
    if (form[fieldName].collection && form[fieldName].hiddenIndex.length) {
      if (_.every(form[fieldName].hiddenIndex, (v) => v === true)) {
        form[fieldName].hidden = true;
        form[fieldName].allHidden = true;
      } else {
        form[fieldName].hidden = false;
        form[fieldName].allHidden = false;
      }
    }

    // this.resetFieldValue(fieldName, dataIndex);
    form[fieldName].needsValidation = false;

    return this;
  }

  /**
   * Sets meta data to disable a field.
   *
   * @param {String} fieldName Field name.
   * @param {String} dataIndex Index for a collection.
   * @return {FormManager}
   */
  enable(fieldName, dataIndex) {
    const form = this.getData();

    if (dataIndex !== undefined) {
      form[fieldName].disabledIndex[dataIndex] = false;
    } else {
      form[fieldName].disabled = false;
    }

    return this;
  }

  /**
   * Sets meta data to disable a field.
   *
   * @param {String} fieldName Field name.
   * @param {String} dataIndex Index for a collection.
   * @return {FormManager}
   */
  disable(fieldName, dataIndex) {
    const form = this.getData();

    if (dataIndex !== undefined) {
      form[fieldName].disabledIndex[dataIndex] = true;
    } else {
      form[fieldName].disabled = true;
    }

    return this;
  }

  calculated(fieldName, dataIndex) {
    const form = this.getData();

    if (dataIndex !== undefined) {
      form[fieldName].calculatedIndex[dataIndex] = true;
    } else {
      form[fieldName].calculated = true;
    }

    return this;
  }

  entered(fieldName, dataIndex) {
    const form = this.getData();

    if (dataIndex !== undefined) {
      form[fieldName].calculatedIndex[dataIndex] = false;
    } else {
      form[fieldName].calculated = false;
    }

    return this;
  }

  /**
   * Helper method to iterate through the fields.
   *
   * @param {Function} cb Callback.
   * @return {FormManager}
   */
  eachField(cb = () => {}) {
    const form = this.getData();
    Object.keys(form).forEach((fieldName) => {
      cb(fieldName, form[fieldName]);
    });

    return this;
  }

  getWarnings() {
    const form = this.getData();
    const warnings = [];

    Object.keys(form).forEach((fieldName) => {
      if (form[fieldName].collection) {
        const warningList = form[fieldName].warningIndex || [];
        const warningText = [];
        warningList.forEach((warning, index) => {
          if (warning) {
            warningText.push(`${warning} (${index + 1})`);
          }
        });

        if (warningText.length) {
          warnings.push({
            label: form[fieldName].label,
            text: warningText.join('. '),
          });
        }
      } else {
        if (form[fieldName].warning) {
          warnings.push({
            label: form[fieldName].label,
            text: form[fieldName].warning,
          });
        }
      }
    });

    return warnings;
  }

  /**
   * Indicates whether for the form is dirty at all.
   *
   * @return {Boolean}
   */
  isDirty() {
    const form = this.getData();

    let dirty = false;
    Object.keys(form).forEach((fieldName) => {
      // Aggregate & Auto Calculate fields don't count
      if (form[fieldName].calculated || form[fieldName].noSubmit) {
        return;
      }

      if (form[fieldName].dirty === true) {
        dirty = true;
      }
    });

    return dirty;
  }

  whatIsDirty() {
    const form = this.getData();
    const dirtyFields = [];

    Object.keys(form).forEach((fieldName) => {
      // Aggregate & Auto Calculate fields don't count
      if (form[fieldName].calculated) {
        return;
      }
      if (form[fieldName].dirty === true) {
        dirtyFields.push(fieldName);
      }
    });

    return dirtyFields;
  }

  // Experimental
  addHistory(fieldName) {
    if (!this.history[fieldName]) {
      this.history[fieldName] = [];
    }

    this.history[fieldName].push({
      data: _.cloneDeep(this.getValue(fieldName)),
    });
  }

  getHistory(fieldName) {
    return this.history[fieldName] || [];
  }

  getTableManager(tableName) {
    return this.tables[tableName];
  }

  getTableConfig(tableName) {
    return this.layout.lookup[tableName];
  }

  /**
   * Checks for empty data where 0 is valid as a value
   *
   * @param {String} value Field value.
   * @return {Boolean}
   */
  static isEmpty(value) {
    return value === undefined || value === null || value === '';
  }

  /**
   * Creates a unique id for the field. Using the field name would usually
   * make the most sense, but the field names are obscure and it can make writing
   * integration tests difficult.
   *
   * @param {String} label Field label.
   * @return {Boolean}
   */
  generateId(label) {
    let id = `${this.prefix}${Layout.sanitizeKey(label)}`;

    let index = 0;
    while (this.ids[id]) {
      id = `${id}-${index}`;
      index++;
    }

    this.ids[id] = true;
    return id;
  }

  /**
   * Determines the type of control based on either an explicit value or
   * key attributes
   *
   * @param {Object} field Field config from page layout.
   * @return {String}
   */
  determineControl(field) {
    const control = (field.control || '').toLowerCase();

    if (!control || control === DEFAULT_CONTROL) {
      return DEFAULT_CONTROL;
    }

    // Explicit control given
    const controlNames = this.getControlNames();
    if (!controlNames.includes(control) && field.type !== 'element') {
      console.error(
        `Invalid control ${field.control} given for field ${field.name}. Valid controls are: ${controlNames.join(',')}.`
      );
      return DEFAULT_CONTROL;
    }

    return control;
  }

  static warnFieldName(fieldName) {
    console.warn(`Invalid field name: '${fieldName}'`);
  }
}

export default FormManager;
